/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React from "react";
import { useMutation } from "@apollo/client";
import css from "classnames";
import { union, values } from "lodash";
import { Icon, Grid, Typography, IconButton } from "@mui/material";
import { Button } from "components/haven-design/buttons";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { userLoginToken } from "utils/general-utils";
import { putFile } from "utils/put-file";
import {
  Actions,
  Document,
  HTTPStatus,
  reducer,
} from "pages/dashboard/interests-documents/reducer";
import { validate } from "pages/dashboard/interests-documents/utils";
import { ALLOWED_FILE_TYPES, CATEGORIES } from "pages/dashboard/interests-documents/constants";
import {
  DOCUMENT_COMPLETE,
  DOCUMENT_PREFLIGHT,
} from "pages/dashboard/interests-documents/query";
import FileUploadButton from "components/haven-design/file-upload-button";
import {
  DocumentsUploadComplete,
  DocumentsUploadCompleteVariables,
  RequestDocumentPreflights,
  RequestDocumentPreflightsVariables,
} from "types/gql-types";
import styles from "./styles.module.css";

const exts = union(...ALLOWED_FILE_TYPES.map((ft) => ft.ext));


const optionToCategory = (option: string | null) =>
  CATEGORIES.find((category) => category.name === option);

type UploaderProps = { onUploaded?: () => void };

const UploadInline: React.FC<UploaderProps> = ({ onUploaded }) => {
  const [preflight] = useMutation<RequestDocumentPreflights, RequestDocumentPreflightsVariables>(
    DOCUMENT_PREFLIGHT,
  );

  const [complete] = useMutation<DocumentsUploadComplete, DocumentsUploadCompleteVariables>(
    DOCUMENT_COMPLETE,
    { refetchQueries: ["UserDocuments"] },
  );

  // Local state and associated dispatcher for managing the state associated
  // with selecting documents from the local filesystem, selecting categories,
  // and any related request statuses.
  const [state, dispatch] = React.useReducer(reducer, {});
  const [fileInputKey, setFileInputKey] = React.useState(0);

  // The local selected files state modified with any potential validation
  // issues.
  const docs = values(validate(state));

  // Filter function returns only selected documents that are valid and haven't
  // been uploaded yet.
  const uploadable = (doc: Document) => doc.validation === null && doc.httpStatus === null;

  const isAnyUploading = docs.some((doc) => doc.httpStatus === HTTPStatus.Loading);
  // convert to are all uploadable
  const areAllUploadable = docs.filter(uploadable).length === docs.length;

  // onSubmitUpload manages the dance thats initiated when the selected
  // documents are submitted for upload. The following high-level requests
  // occur:
  //  1. Sends one GraphQL mutation for creating one or more document preflight
  //    requests. This mutation returns a list of the document objects, the
  //    temporary ID associated with that request, and a pre-signed S3 URL
  //    which will be used when uploading the document contents to S3.
  //  2. Once the preflight request is completed, one or more HTTP requests
  //    are started to the S3 service, each containing the contents of one of
  //    the documents.
  //  3. Once all S3 uploads are completed, the documents which were
  //    successfully uploaded are then collected and sent in one last GraphQL
  //    mutation to mark them as having been successfully uploaded.
  //  4. Finally, once the uploaded documents are marked as completed, the
  //    query to fetch all uploaded documents for the current interest is
  //    performed and the UI is updated to reflect the newly uploaded
  //    documents.
  const onSubmitUpload = () => {
    const token = userLoginToken();
    if (token === null) {
      throw new Error("Unexpected absence of the user's login token.");
    }

    // Create a set of preflight document request objects which will be passed
    // as a variable to the document preflight GraphQL mutation.
    const documents = docs.filter(uploadable).map((document) => ({
      tempId: document.id,
      filename: document.file.name,
      filesize: document.file.size,
      category: document.category as string,
      interest: null,
    }));

    // Update the local state of the selected documents as having begun their
    // upload journey.
    dispatch({
      type: Actions.StartPreflight,
      payload: { documents: documents.map((document) => document.tempId) },
    });

    // Perform the document preflight request. Once completed, individual HTTP
    // requests will be performed with the returned pre-signed S3 URLs.
    preflight({ variables: { documents } }).then((response) => {
      if (!response.data) {
        return;
      }

      const { documentPreflight } = response.data;

      const requests = documentPreflight.map(
        ({ uploadUrl, tempId, document }) =>
          new Promise<string>((resolve, reject) => {
            if (!(tempId in state)) {
              throw new Error("Unexpected response from the server.");
            }

            putFile(uploadUrl, state[tempId].file)
              .then((uploadResponse) => {
                dispatch({
                  type: Actions.S3UploadResponse,
                  payload: { id: tempId, response: uploadResponse },
                });

                if (uploadResponse.status >= 200 && uploadResponse.status < 300) {
                  resolve(document.id);
                  return;
                }

                reject(document.id);
              })
              .catch(() => {
                dispatch({
                  type: Actions.S3UploadResponse,
                  payload: { id: tempId, response: new Response(null, { status: 503 }) },
                });
                reject(document.id);
              });
          }),
      );

      // The document already has its HTTP status. Handle the rejection so a failed
      // PUT does not become an unhandled Promise.all rejection, and a failed upload
      // is not cleared by the success reset below.
      Promise.all(requests)
        .then((uploadedDocuments) => {
          complete({ variables: { documents: uploadedDocuments } }).then(() => {
            dispatch({ type: Actions.ResetSelection, payload: {} });
            setFileInputKey(fileInputKey + 1);
            if (onUploaded) onUploaded();
          });
        })
        .catch(() => undefined);
    });
  };

  const onFileInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    dispatch({
      type: Actions.SelectFiles,
      payload: {
        files: values(target.files),
      },
    });
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        We accept {exts.join(", ")} file types.
      </Typography>

      <FileUploadButton
        key={fileInputKey}
        id="upload"
        name="upload"
        accept={exts.join(", ")}
        multiple
        onChange={onFileInputChange}
      />

      <Grid container spacing={1}>
        {docs.map((document) => (
          <Grid key={document.id} size={{ xs: 12 }}>
            <Grid size={{ xs: 12, sm: 8, md: 5 }}
              container
              className={css({}, {
                success: document.httpStatus === HTTPStatus.Success,
                danger: document.httpStatus === HTTPStatus.Error,
                [styles.shimmer]: document.httpStatus === HTTPStatus.Loading,
              })}
            >
              <Grid size={{ xs: 11, sm: 11 }} className={styles.fileRowLeft}>
                <Typography title={document.file.name} sx={{ wordBreak: "break-word" }}>
                  {document.file.name}
                </Typography>

                <SelectAutocomplete
                  margin="dense"
                  label="Select a Category"
                  disableClearable
                  disabled={document.httpStatus !== null}
                  options={CATEGORIES}
                  getOptionLabel={(option) => option.name}
                  groupBy={({ group }) => group}
                  value={optionToCategory(document.category)}
                  data-cy="select-category"
                  onChange={(_, value) => {
                    if (value) {
                      dispatch({
                        type: Actions.ChangeFileCategory,
                        payload: { id: document.id, category: value.name },
                      });
                    }
                  }}
                />

                <div>
                  {document.httpStatus === HTTPStatus.Success && (
                    <div className="label label-success">Uploaded</div>
                  )}
                  {document.validation !== null && (
                    <div className="label label-danger">{document.validation}</div>
                  )}
                </div>
              </Grid>
              <Grid size={{ xs: 1, sm: 1 }} className={styles.fileRowRight}>
                {document.httpStatus === null && (
                  <IconButton
                    aria-label="remove"
                    onClick={() => {
                      dispatch({
                        type: Actions.RemoveFile,
                        payload: { id: document.id },
                      });
                      if (Object.keys(state).length === 1) {
                        setFileInputKey(fileInputKey + 1);
                      }
                    }}
                    size="small"
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Grid>
        ))}

      </Grid>

      {docs.length ? (
        <div style={{ marginTop: 30 }}>
          <Button
            size="S"
            color="secondary"
            marginRight
            disabled={docs.length === 0 || isAnyUploading}
            onClick={() => {
              const msg = "You have not sent your files. Are you sure you want to exit?";
              // eslint-disable-next-line no-alert
              if (confirm(msg)) {
                dispatch({ type: Actions.ResetSelection, payload: {} });
                setFileInputKey(fileInputKey + 1);
              }
            }}
          >
          Cancel
          </Button>
          <Button size="S" color="primary" onClick={onSubmitUpload} disabled={!areAllUploadable}>
          Submit uploaded files
          </Button>
        </div>
      ) : (
        <div className="emptyContainer" />
      )}
    </>
  );
};

export default UploadInline;
